import { ClientOptionsStore } from "../ClientOptions/ClientOptionsStore";
import { ClientImpl } from "../Client/ClientImpl/ClientImpl";
import { Client, ClientOptions } from "~/modules/client";
import { mergeUserOptions } from "~/utils/mergeUserOptions";
import { getLogger, LoggerConfigImpl, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity, FlexSdkErrorMetadata } from "~/modules/error";
import type { DeepPartial } from "~/utils/DeepPartial";
import {
    throwFlexSdkError,
    throwFlexSdkErrorFromErrorResponse,
    ErrorResponse
} from "~/modules/error/ThrowError/ErrorHelper";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { initErrorSubscriptions } from "~/modules/reporter/Subscriber";
import { featuresProviderImpl } from "~/modules/config/FeaturesProvider/FeaturesProviderImpl/FeaturesProviderImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { TelemetryImpl } from "~/modules/telemetry/TelemetryImpl";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { configureSDKTelemetry, sendLifecycleTrackingEvent } from "~/modules/telemetry/utils";

const createSessionErrorHandler =
    (
        throwError: ReturnType<typeof throwFlexSdkError>,
        throwErrorFromErrorResponse: ReturnType<typeof throwFlexSdkErrorFromErrorResponse>
    ) =>
    (err: unknown) => {
        const metadata: FlexSdkErrorMetadata = {
            module: "client",
            severity: ErrorSeverity.Error,
            source: "createClient.ts"
        };

        const errorWithCode = err as { code?: number; source?: string };

        
        if (errorWithCode.code === 51102 || errorWithCode.code === 20003) {
            metadata.source = "Twilsock";
            const sourceError = errorWithCode.source ? new Error(errorWithCode.source) : undefined;
            throwError(ErrorCode.AccessDeniedError, metadata, "Insufficient permissions", sourceError);
        } else {
            throwErrorFromErrorResponse(err as ErrorResponse, metadata);
        }
    };

const sendInitializationTelemetry = (
    startTimeMs: number,
    analytics: AnalyticsImpl,
    session: SessionImpl,
    logger: ReturnType<ReturnType<typeof getLogger>>
) => {
    try {
        const durationMs = Date.now() - startTimeMs;
        logger.debug(`FlexSDK Client initialization took ${durationMs}ms`);
        sendLifecycleTrackingEvent(EVENTS.ClientInitialized, analytics, session.accountSid, {
            durationMs
        });
    } catch (e) {
        logger.error("Failed to set session data for telemetry", e);
    }
};

const initializeFeatureProvider = (ctx: ContextManager, token: string, clientOptions: ClientOptionsStore) => {
    return !clientOptions.telemetryOptions.disabled ? featuresProviderImpl(ctx)(token) : Promise.resolve(undefined);
};

const initializeSession = (session: SessionImpl, token: string, errorHandler: (err: Error) => void) => {
    
    return session.token ? Promise.resolve() : session.init(token).catch(errorHandler);
};

export const createClient =
    (ctx: ContextManager) =>
    async (token: string, userOptions?: DeepPartial<ClientOptions>): Promise<Client> => {
        const startTimeMs = Date.now();
        initErrorSubscriptions(ctx);

        const clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        mergeUserOptions(clientOptions, userOptions);

        const loggerConfig = ctx.retrieveInstanceOf(ContextInstance.LoggerConfigImpl, LoggerConfigImpl);
        loggerConfig.level = clientOptions.logger.level;

        const session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const analytics = ctx.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const telemetry = ctx.retrieveInstanceOf(ContextInstance.TelemetryImpl, TelemetryImpl);
        const client = ctx.retrieveInstanceOf(ContextInstance.ClientImpl, ClientImpl);
        const logger = getLogger(ctx)(LoggerName.Client);
        const throwError = throwFlexSdkError(ctx);
        const throwErrorFromErrorResponse = throwFlexSdkErrorFromErrorResponse(ctx);

        logger.debug("Starting FlexSDK Client initialization");

        const initializationPromises = await Promise.allSettled([
            initializeSession(session, token, createSessionErrorHandler(throwError, throwErrorFromErrorResponse)),
            initializeFeatureProvider(ctx, token, clientOptions)
        ]);

        const [sessionResult, featuresResult] = initializationPromises;

        if (sessionResult.status === "rejected") {
            throw sessionResult.reason;
        }

        const featuresConfig = featuresResult.status === "fulfilled" ? featuresResult.value : undefined;

        configureSDKTelemetry(
            analytics,
            telemetry,
            logger,
            clientOptions.telemetryOptions.disabled,
            featuresConfig,
            session.accountSid
        );
        sendInitializationTelemetry(startTimeMs, analytics, session, logger);

        logger.debug("FlexSDK Client constructed");
        return client;
    };
